import { apiUrl, exportData, getList } from './apiUtils';
import type { ApiListParamsWithUnderscores, ApiExportParams } from '../types/api/listParams';
import type { ListApiResponse } from '../types/apiResponse';

const baseUrl = apiUrl + '/reports/';
const exportBaseUrl = `${apiUrl}/export/reports/`;

export function getRequestsByMonthByGame(
  year: number,
  company?: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/month/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getRequestsByMonthBySKU(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/month/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByMonthByGame(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/month/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByMonthBySku(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/month/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function getKeysByMonthByCompany(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/month/company?year=${year}`, params);
}

export function getRequestsByMonthByCompany(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/month/company?year=${year}`, params);
}

export function getRequestsByMonthByAuthorisingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/month/authorisinguser?year=${year}`, params);
}

export function exportRequestsByMonthByAuthorisingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/month/authorisinguser?year=${year}`, 'fullName', params);
}

export function getKeysByMonthByAuthorisingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/month/authorisinguser?year=${year}`, params);
}

export function exportKeysByMonthByAuthorisingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/month/authorisinguser?year=${year}`, 'fullName', params);
}

export function getRequestsByMonthByUploadingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}requests/month/uploadinguser?year=${year}`, params);
}

export function exportRequestsByMonthByUploadingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/month/uploadinguser?year=${year}`, 'fullName', params);
}

export function getKeysByMonthByUploadingUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keys/month/uploadinguser?year=${year}`, params);
}

export function exportKeysByMonthByUploadingUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/month/uploadinguser?year=${year}`, 'fullName', params);
}

export function exportRequestsByMonthByGame(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/month/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportRequestsByMonthBySKU(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/month/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByMonthByGame(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/month/game?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByMonthBySku(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/month/sku?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'game', params ?? { filters: {}, scopes: {} });
}

export function exportKeysByMonthByCompany(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keys/month/company?year=${year}`, 'companyName', params);
}

export function exportRequestsByMonthByCompany(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}requests/month/company?year=${year}`, 'companyName', params);
}

export function exportSLAsByCompany(params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}sla/company`, 'companyName', params);
}

export function getUsersModifiedByMonthByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}users/month/user?year=${year}`, params);
}

export function exportUsersModifiedByMonthByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}users/month/user?year=${year}`, 'fullName', params);
}

export function getKeyWorkflowsModifiedByMonthByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}keyworkflows/month/user?year=${year}`, params);
}

export function exportKeyWorkflowsModifiedByMonthByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}keyworkflows/month/user?year=${year}`, 'fullName', params);
}

export function getGamesModifiedByMonthByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}games/month/user?year=${year}`, params);
}

export function exportGamesModifiedByMonthByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}games/month/user?year=${year}`, 'fullName', params);
}

export function getSKUsModifiedByMonthByUser(year: number, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}skus/month/user?year=${year}`, params);
}

export function exportSKUsModifiedByMonthByUser(year: number, params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}skus/month/user?year=${year}`, 'fullName', params);
}

export function getRequestsByMonthByWorkflow(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}requests/month/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function exportRequestsByMonthByWorkflow(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}requests/month/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'workflow', params ?? { filters: {}, scopes: {} });
}
export function getKeysByMonthByWorkflow(year: number, company?: string, params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  let url = `${baseUrl}keys/month/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return getList(url, params);
}

export function exportKeysByMonthByWorkflow(year: number, company?: string, params?: ApiExportParams
): Promise<void> {
  let url = `${exportBaseUrl}keys/month/workflow?year=${year}`;
  if (company) {
    url += `&companyId=${company}`;
  }
  return exportData(url, 'workflow', params ?? { filters: {}, scopes: {} });
}
